/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";

import { LocaleKeys } from "../../../model/locale";
import { localizedErrorFallback } from "../../../model/utility";
import type { SignInState } from "../signIn.types";
import {
  sendForgotPasswordLink,
  submitUpdatedPassword,
  validateResetEmailToken
} from "../thunks/passwordReset.thunks";

/**
 * Case reducers for the forgot/reset password screens.
 */
export const passwordResetReducers = {
  setRecoveryEmail(state: SignInState, action: PayloadAction<string>): void {
    state.recoveryEmail = action.payload;
  },
  resetPasswordFlowState(state: SignInState): void {
    state.recoveryEmail = "";
    state.isSendingResetLink = false;
    state.sendResetLinkError = "";
    state.isResetTokenValidating = false;
    state.resetTokenValidationError = "";
    state.resetIdentityPayload = null;
    state.isUpdatingPassword = false;
    state.updatePasswordError = "";
    state.isPasswordResetComplete = false;
  }
};

/**
 * Reducers for the three-step browser password reset: send link,
 * validate the emailed token, submit the new password.
 */
export function registerPasswordResetReducers(
  builder: ActionReducerMapBuilder<SignInState>
): void {
  builder.addCase(sendForgotPasswordLink.pending, (state) => {
    state.isSendingResetLink = true;
    state.sendResetLinkError = "";
  });
  builder.addCase(sendForgotPasswordLink.fulfilled, (state, action) => {
    state.isSendingResetLink = false;
    state.sendResetLinkError = "";
    state.recoveryEmail = action.payload;
  });
  builder.addCase(sendForgotPasswordLink.rejected, (state, action) => {
    state.isSendingResetLink = false;
    state.sendResetLinkError =
      action.payload !== undefined && action.payload !== null
        ? action.payload
        : localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_LINK_SEND_ERROR_MESSAGE
          );
  });
  builder.addCase(validateResetEmailToken.pending, (state) => {
    state.isResetTokenValidating = true;
    state.resetTokenValidationError = "";
    state.resetIdentityPayload = null;
    state.isPasswordResetComplete = false;
  });
  builder.addCase(validateResetEmailToken.fulfilled, (state, action) => {
    state.isResetTokenValidating = false;
    state.resetTokenValidationError = "";
    state.resetIdentityPayload = action.payload;
  });
  builder.addCase(validateResetEmailToken.rejected, (state, action) => {
    state.isResetTokenValidating = false;
    state.resetIdentityPayload = null;
    state.resetTokenValidationError =
      action.payload !== undefined && action.payload !== null
        ? action.payload
        : localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_LINK_INVALID_ERROR_MESSAGE
          );
  });
  builder.addCase(submitUpdatedPassword.pending, (state) => {
    state.isUpdatingPassword = true;
    state.updatePasswordError = "";
    state.isPasswordResetComplete = false;
  });
  builder.addCase(submitUpdatedPassword.fulfilled, (state) => {
    state.isUpdatingPassword = false;
    state.updatePasswordError = "";
    state.isPasswordResetComplete = true;
  });
  builder.addCase(submitUpdatedPassword.rejected, (state, action) => {
    state.isUpdatingPassword = false;
    state.updatePasswordError =
      action.payload !== undefined && action.payload !== null
        ? action.payload
        : localizedErrorFallback(
            LocaleKeys.SIGN_IN_RESET_PASSWORD_UPDATE_ERROR_MESSAGE
          );
    state.isPasswordResetComplete = false;
  });
}
